import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, FileText, Brain, CheckSquare, BookOpen,
    Settings, Clock, Shield, Save, Check, ChevronRight,
    ChevronLeft, AlertCircle, Calendar, Tag, Loader2,
    Layers, Sparkles
} from 'lucide-react';
import CombinedTaskBuilder from '../Tasks/CombinedTaskBuilder';
import AssignmentPlaceholderPicker from './AssignmentPlaceholderPicker';
import PlaceholderTextarea from './PlaceholderTextarea';

// ─── Helper: task-type icon ──────────────────────────────────────────────────
const getTaskIcon = (taskType) => {
    switch (taskType) {
        case 'quiz': return <Brain className="w-5 h-5" />;
        case 'training': return <BookOpen className="w-5 h-5" />;
        case 'checklist': return <CheckSquare className="w-5 h-5" />;
        default: return <FileText className="w-5 h-5" />;
    }
};

// ─── Helper: task-type color config ──────────────────────────────────────────
const getTaskTypeConfig = (taskType) => {
    switch (taskType) {
        case 'quiz':
            return { bg: 'bg-purple-100 dark:bg-purple-500/20', text: 'text-purple-600 dark:text-purple-400', label: 'Quiz', border: 'border-purple-200 dark:border-purple-500/30' };
        case 'training':
            return { bg: 'bg-blue-100 dark:bg-blue-500/20', text: 'text-blue-600 dark:text-blue-400', label: 'Training', border: 'border-blue-200 dark:border-blue-500/30' };
        case 'checklist':
            return { bg: 'bg-green-100 dark:bg-green-500/20', text: 'text-green-600 dark:text-green-400', label: 'Checklist', border: 'border-green-200 dark:border-green-500/30' };
        case 'combined':
            return { bg: 'bg-violet-100 dark:bg-violet-500/20', text: 'text-violet-600 dark:text-violet-400', label: 'Combined', border: 'border-violet-200 dark:border-violet-500/30' };
        default:
            return { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', label: 'Task', border: 'border-gray-200 dark:border-slate-600' };
    }
};

// ═══════════════════════════════════════════════════════════════════════════════
// TaskDetailEditor — inline task detail page within AICourseGenerator
// ═══════════════════════════════════════════════════════════════════════════════
const TaskDetailEditor = ({ task, timelineItem, onSave, onBack }) => {
    // ── Local form state (deep copy from task prop) ──────────────────────────
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_type: 'task',
        content: '',
        category: '',
        priority: 'medium',
        estimated_duration: 30,
        requires_proof: false,
        requires_signature: false,
        passing_score: 70,
        quiz_grading_type: 'auto',
        allow_retakes: false,
        max_attempts: 1,
        due_days_after_assignment: 7,
        is_compliance_required: false,
        is_combined: false,
        sections: [],
        assignment_placeholders: []
    });

    const [activeStep, setActiveStep] = useState('content');
    const [hasChanges, setHasChanges] = useState(false);
    const [isAnimatingIn, setIsAnimatingIn] = useState(true);

    // Steps
    const steps = ['content', 'settings'];
    const currentStepIndex = steps.indexOf(activeStep);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // ── Initialize from task prop ────────────────────────────────────────────
    useEffect(() => {
        if (task) {
            setFormData({
                title: task.title || '',
                description: task.description || '',
                task_type: task.task_type || 'task',
                content: task.content || '',
                category: task.category || '',
                priority: task.priority || 'medium',
                estimated_duration: task.estimated_duration || 30,
                requires_proof: task.requires_proof || false,
                requires_signature: task.requires_signature || false,
                passing_score: task.passing_score || 70,
                quiz_grading_type: task.quiz_grading_type || 'auto',
                allow_retakes: task.allow_retakes || false,
                max_attempts: task.max_attempts || 1,
                due_days_after_assignment: task.due_days_after_assignment || 7,
                is_compliance_required: task.is_compliance_required || false,
                is_combined: task.is_combined !== undefined ? task.is_combined : true,
                sections: task.sections || [],
                assignment_placeholders: timelineItem?.assignment_placeholders || task.assignment_placeholders || []
            });
            setHasChanges(false);
        }
    }, [task, timelineItem]);

    // Entrance animation
    useEffect(() => {
        const timer = setTimeout(() => setIsAnimatingIn(false), 300);
        return () => clearTimeout(timer);
    }, []);

    // ── Change handlers ──────────────────────────────────────────────────────
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
        }));
        setHasChanges(true);
    }, []);

    const handleSectionsChange = useCallback((sections) => {
        setFormData(prev => ({ ...prev, sections }));
        setHasChanges(true);
    }, []);

    // ── Apply changes & go back ──────────────────────────────────────────────
    const handleApplyAndBack = useCallback(() => {
        onSave(formData);
        onBack();
    }, [formData, onSave, onBack]);

    const handleBackWithoutSaving = useCallback(() => {
        if (hasChanges) {
            if (window.confirm('You have unsaved changes. Discard them?')) {
                onBack();
            }
        } else {
            onBack();
        }
    }, [hasChanges, onBack]);

    // ── Derived ──────────────────────────────────────────────────────────────
    const typeConfig = getTaskTypeConfig(formData.task_type);
    const triggerDay = timelineItem?.trigger_day ?? timelineItem?.relative_day ?? '?';

    // ═════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════════════════════
    return (
        <div className={`flex flex-col h-full bg-white dark:bg-slate-900 transition-all duration-300 ${isAnimatingIn ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>

            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                {/* Back + Title row */}
                <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
                    <button
                        onClick={handleBackWithoutSaving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-lg transition-all group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                        Timeline
                    </button>

                    <div className="w-px h-6 bg-gray-200 dark:bg-slate-700" />

                    {/* Task type badge + title */}
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${typeConfig.bg} ${typeConfig.text} flex-shrink-0`}>
                            {getTaskIcon(formData.task_type)}
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-semibold text-gray-800 dark:text-slate-100 truncate text-sm sm:text-base">
                                {formData.title || 'Untitled Task'}
                            </h2>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase ${typeConfig.bg} ${typeConfig.text}`}>
                                    {typeConfig.label}
                                </span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Day {triggerDay}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Apply Changes button */}
                    <button
                        onClick={handleApplyAndBack}
                        disabled={!formData.title?.trim()}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${hasChanges
                            ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white shadow-lg shadow-violet-500/25'
                            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {hasChanges ? (
                            <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">Apply Changes</span>
                                <span className="sm:hidden">Apply</span>
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">No Changes</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Step Progress Indicator */}
                <div className="flex items-center gap-2 px-4 sm:px-6 pb-3">
                    {[
                        { id: 'content', label: 'Content', icon: Layers, step: 1 },
                        { id: 'settings', label: 'Settings', icon: Settings, step: 2 }
                    ].map(({ id, label, icon: Icon, step }, index) => {
                        const stepIdx = steps.indexOf(id);
                        const isCompleted = stepIdx < currentStepIndex;
                        const isCurrent = id === activeStep;

                        return (
                            <React.Fragment key={id}>
                                <button
                                    onClick={() => setActiveStep(id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isCurrent
                                        ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-500/30'
                                        : isCompleted
                                            ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10'
                                            : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isCompleted
                                        ? 'bg-green-500 text-white'
                                        : isCurrent
                                            ? 'bg-violet-600 text-white'
                                            : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                        }`}>
                                        {isCompleted ? <Check className="w-3 h-3" /> : step}
                                    </div>
                                    <span className="hidden sm:inline">{label}</span>
                                </button>
                                {index < steps.length - 1 && (
                                    <div className={`w-6 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-700'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* ── Content Area ───────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {activeStep === 'content' && (
                    <div className="space-y-5">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter task title"
                                className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent text-lg"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Description
                            </label>
                            <PlaceholderTextarea
                                value={formData.description}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, description: e.target.value }));
                                    setHasChanges(true);
                                }}
                                placeholder="Brief description of this task — use {employee.name} etc. for personalization"
                                rows={3}
                            />
                        </div>

                        {/* Quiz-specific: Grading Type */}
                        {formData.task_type === 'quiz' && (
                            <div className="p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">
                                    Grading Type
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => { setFormData(prev => ({ ...prev, quiz_grading_type: 'auto' })); setHasChanges(true); }}
                                        className={`p-3 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${formData.quiz_grading_type === 'auto'
                                            ? 'border-green-500 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-gray-300'
                                            }`}
                                    >
                                        <Brain className="w-5 h-5" />
                                        <span className="font-medium text-sm">Auto-Graded</span>
                                        <span className="text-xs text-gray-500 dark:text-slate-400">Multiple choice</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setFormData(prev => ({ ...prev, quiz_grading_type: 'manual' })); setHasChanges(true); }}
                                        className={`p-3 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${formData.quiz_grading_type === 'manual'
                                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-gray-300'
                                            }`}
                                    >
                                        <FileText className="w-5 h-5" />
                                        <span className="font-medium text-sm">Manual Review</span>
                                        <span className="text-xs text-gray-500 dark:text-slate-400">Written answers</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* CombinedTaskBuilder */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Content
                            </label>
                            <CombinedTaskBuilder
                                sections={formData.sections}
                                onChange={handleSectionsChange}
                            />
                        </div>

                        {/* Assignment Placeholders */}
                        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                            <AssignmentPlaceholderPicker
                                value={formData.assignment_placeholders || []}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, assignment_placeholders: val }));
                                    setHasChanges(true);
                                }}
                            />
                        </div>
                    </div>
                )}

                {activeStep === 'settings' && (
                    <div className="space-y-5">
                        {/* Basic Settings Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Category */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                    Category
                                </label>
                                <div className="relative">
                                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                    <input
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        placeholder="e.g., Compliance, IT Setup"
                                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {/* Priority */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                    Priority
                                </label>
                                <select
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>

                            {/* Estimated Duration */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                    Estimated Duration
                                </label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                    <input
                                        type="number"
                                        name="estimated_duration"
                                        value={formData.estimated_duration}
                                        onChange={handleChange}
                                        min="5"
                                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-slate-500">min</span>
                                </div>
                            </div>

                            {/* Due Days */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                    Due Days After Assignment
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                    <input
                                        type="number"
                                        name="due_days_after_assignment"
                                        value={formData.due_days_after_assignment}
                                        onChange={handleChange}
                                        min="1"
                                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-slate-500">days</span>
                                </div>
                            </div>

                            {/* Passing Score (Quiz only) */}
                            {formData.task_type === 'quiz' && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                        Passing Score
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="passing_score"
                                            value={formData.passing_score}
                                            onChange={handleChange}
                                            min="0"
                                            max="100"
                                            className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-slate-500">%</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Toggles */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200">Options</h3>

                            {/* Requires Proof */}
                            <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                                <input
                                    type="checkbox"
                                    name="requires_proof"
                                    checked={formData.requires_proof}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-800 dark:text-slate-100">Requires Proof</span>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">Employee must upload proof of completion</p>
                                </div>
                            </label>

                            {/* Requires Signature */}
                            <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                                <input
                                    type="checkbox"
                                    name="requires_signature"
                                    checked={formData.requires_signature}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-800 dark:text-slate-100">Requires Signature</span>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">Employee must sign to acknowledge completion</p>
                                </div>
                            </label>

                            {/* Quiz-specific: Allow Retakes */}
                            {formData.task_type === 'quiz' && (
                                <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                                    <input
                                        type="checkbox"
                                        name="allow_retakes"
                                        checked={formData.allow_retakes}
                                        onChange={handleChange}
                                        className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
                                    />
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-800 dark:text-slate-100">Allow Retakes</span>
                                        <p className="text-xs text-gray-500 dark:text-slate-400">Employee can reattempt the quiz</p>
                                    </div>
                                    {formData.allow_retakes && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-500 dark:text-slate-400">Max:</span>
                                            <input
                                                type="number"
                                                name="max_attempts"
                                                value={formData.max_attempts}
                                                onChange={handleChange}
                                                min="1"
                                                max="10"
                                                className="w-14 px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </div>
                                    )}
                                </label>
                            )}
                        </div>

                        {/* Compliance Toggle */}
                        <div className={`rounded-xl p-4 border-2 transition-all ${formData.is_compliance_required
                            ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-500/10 dark:to-orange-500/10 border-red-200 dark:border-red-500/30'
                            : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700'
                            }`}>
                            <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${formData.is_compliance_required
                                    ? 'bg-red-100 dark:bg-red-500/20'
                                    : 'bg-gray-200 dark:bg-slate-700'
                                    }`}>
                                    <Shield className={`w-5 h-5 ${formData.is_compliance_required
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-gray-500 dark:text-slate-400'
                                        }`} />
                                </div>
                                <div className="flex-1">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="is_compliance_required"
                                            checked={formData.is_compliance_required}
                                            onChange={handleChange}
                                            className={`w-4 h-4 rounded focus:ring-2 ${formData.is_compliance_required
                                                ? 'text-red-600 focus:ring-red-500'
                                                : 'text-violet-600 focus:ring-violet-500'
                                                }`}
                                        />
                                        <span className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                                            This is a compliance-required task
                                        </span>
                                    </label>
                                    <p className="text-xs text-gray-600 dark:text-slate-300 mt-1 ml-6">
                                        Compliance tasks are legally required and tracked separately. Overdue tasks create violations.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Footer — Step Navigation ───────────────────────────────── */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 px-4 sm:px-6 py-3">
                <div className="flex items-center justify-between">
                    {/* Left: Status */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                        {hasChanges ? (
                            <>
                                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                <span className="text-xs">Unsaved changes</span>
                            </>
                        ) : (
                            <>
                                <div className="w-2 h-2 bg-green-500 rounded-full" />
                                <span className="text-xs">Up to date</span>
                            </>
                        )}
                    </div>

                    {/* Right: Navigation */}
                    <div className="flex items-center gap-2">
                        {!isFirstStep && (
                            <button
                                onClick={() => setActiveStep(steps[currentStepIndex - 1])}
                                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}

                        {!isLastStep && (
                            <button
                                onClick={() => setActiveStep(steps[currentStepIndex + 1])}
                                className="flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors text-sm"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        )}

                        {isLastStep && (
                            <button
                                onClick={handleApplyAndBack}
                                disabled={!formData.title?.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-all shadow-lg shadow-violet-500/25 text-sm font-semibold disabled:opacity-50"
                            >
                                <Check className="w-4 h-4" />
                                Apply & Return
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskDetailEditor;
